// BottomSheetContext.jsx
import { createContext, useState, useContext, ReactNode, useEffect } from 'react'
import VaulDrawer from '../../components/BottomSheetComponents/VaulDrawer'

type BottomSheetContent = ReactNode | (() => ReactNode)

type BottomSheetOpenOptions = {
  drawerTitleId?: string
  focusDrawerOnOpen?: boolean
  dismissible?: boolean
}

type BottomSheetNestedOpenOptions = BottomSheetOpenOptions & {
  /** When false, overlay is not exposed as a dismiss control to AT (nested dialogs). */
  dismissible?: boolean
}

type BottomSheetItem = {
  id: string
  content: BottomSheetContent
  onDismiss?: () => void
  drawerTitleId?: string
  focusDrawerOnOpen?: boolean
  dismissible?: boolean
}

type BottomSheetContextType = {
  isMainBottomSheetOpen: boolean
  isNestedBottomSheetOpen: boolean
  openBottomSheet: (
    content: BottomSheetContent,
    onDismiss?: () => void,
    options?: BottomSheetOpenOptions
  ) => void
  closeBottomSheet: () => void
  destroyBottomSheet: () => void
  openNestedBottomSheet: (
    content: BottomSheetContent,
    onDismiss?: () => void,
    options?: BottomSheetNestedOpenOptions
  ) => void
  closeNestedBottomSheet: () => void
  destroyNestedBottomSheet: () => void
  closeAllBottomSheets: () => void
  destroyAllBottomSheets: () => void
  updateMainBottomSheet: (content: BottomSheetContent) => void
  updateNestedBottomSheet: (content: BottomSheetContent) => void
  mainBottomSheet: BottomSheetItem | null
  nestedBottomSheet: BottomSheetItem | null
} | null

export const BottomSheetContext = createContext<BottomSheetContextType>(null)

export const useBottomSheet = () => {
  const context = useContext(BottomSheetContext)
  if (!context) {
    throw new Error('useBottomSheet must be used within a BottomSheetProvider')
  }
  return context
}

export const BottomSheetProvider = ({ children }: { children: ReactNode }) => {
  const [mainBottomSheet, setMainBottomSheet] = useState<BottomSheetItem | null>(null)
  const [nestedBottomSheet, setNestedBottomSheet] = useState<BottomSheetItem | null>(null)
  const [isMainBottomSheetOpen, setIsMainBottomSheetOpen] = useState(false)
  const [isNestedBottomSheetOpen, setIsNestedBottomSheetOpen] = useState(false)

  const openBottomSheet = (
    content: BottomSheetContent,
    onDismiss?: () => void,
    options?: BottomSheetOpenOptions
  ) => {
    const newSheet: BottomSheetItem = {
      id: `sheet-${Date.now()}-${Math.random()}`,
      content,
      onDismiss,
      drawerTitleId: options?.drawerTitleId,
      focusDrawerOnOpen: options?.focusDrawerOnOpen,
      dismissible: options?.dismissible
    }
    setMainBottomSheet(newSheet)
    setIsMainBottomSheetOpen(true)
  }

  const closeBottomSheet = () => {
    setIsMainBottomSheetOpen(false)
  }

  const destroyBottomSheet = () => {
    setIsMainBottomSheetOpen(false)
    setMainBottomSheet(null)
  }

  const openNestedBottomSheet = (
    content: BottomSheetContent,
    onDismiss?: () => void,
    options?: BottomSheetNestedOpenOptions
  ) => {
    const newSheet: BottomSheetItem = {
      id: `sheet-${Date.now()}-${Math.random()}`,
      content,
      onDismiss,
      drawerTitleId: options?.drawerTitleId,
      focusDrawerOnOpen: options?.focusDrawerOnOpen,
      dismissible: options?.dismissible
    }
    setNestedBottomSheet(newSheet)
    setIsNestedBottomSheetOpen(true)
  }

  const closeNestedBottomSheet = () => {
    setIsNestedBottomSheetOpen(false)
  }

  const destroyNestedBottomSheet = () => {
    setIsNestedBottomSheetOpen(false)
    setNestedBottomSheet(null)
  }

  const closeAllBottomSheets = () => {
    setIsMainBottomSheetOpen(false)
    setIsNestedBottomSheetOpen(false)
    setMainBottomSheet(null)
    setNestedBottomSheet(null)
  }

  const destroyAllBottomSheets = () => {
    setIsMainBottomSheetOpen(false)
    setIsNestedBottomSheetOpen(false)
    setMainBottomSheet(null)
    setNestedBottomSheet(null)
  }

  const updateMainBottomSheet = (content: BottomSheetContent) => {
    if (mainBottomSheet) {
      setMainBottomSheet({
        ...mainBottomSheet,
        content
      })
    }
  }

  const updateNestedBottomSheet = (content: BottomSheetContent) => {
    if (nestedBottomSheet) {
      setNestedBottomSheet({
        ...nestedBottomSheet,
        content
      })
    }
  }

  const handleMainSheetOpenChange = (open: boolean) => {
    if (!open) {
      setIsMainBottomSheetOpen(false)
    }
  }

  const handleNestedSheetOpenChange = (open: boolean) => {
    if (!open) {
      setIsNestedBottomSheetOpen(false)
    }
  }

  useEffect(() => {
    // no-op state debug hook retained for local debugging
  }, [isMainBottomSheetOpen, isNestedBottomSheetOpen, mainBottomSheet, nestedBottomSheet])

  return (
    <BottomSheetContext.Provider
      value={{
        isMainBottomSheetOpen,
        isNestedBottomSheetOpen,
        openBottomSheet,
        closeBottomSheet,
        destroyBottomSheet,
        openNestedBottomSheet,
        closeNestedBottomSheet,
        destroyNestedBottomSheet,
        closeAllBottomSheets,
        destroyAllBottomSheets,
        updateMainBottomSheet,
        updateNestedBottomSheet,
        mainBottomSheet,
        nestedBottomSheet
      }}
    >
      {children}
      {mainBottomSheet && (
        <VaulDrawer
          key={`main-${mainBottomSheet.id}`}
          open={isMainBottomSheetOpen}
          setOpen={handleMainSheetOpenChange}
          zIndex={10000}
          onDismiss={mainBottomSheet?.onDismiss}
          titleId={mainBottomSheet.drawerTitleId}
          overlayHiddenFromAccessibility={isNestedBottomSheetOpen}
          contentHiddenFromAccessibility={isNestedBottomSheetOpen}
          focusDrawerOnOpen={mainBottomSheet.focusDrawerOnOpen}
          dismissible={mainBottomSheet.dismissible ?? true}
        >
          {typeof mainBottomSheet.content === 'function'
            ? (mainBottomSheet.content as Function)()
            : mainBottomSheet.content}
        </VaulDrawer>
      )}

      {nestedBottomSheet && (
        <VaulDrawer
          key={`nested-${nestedBottomSheet.id}`}
          open={isNestedBottomSheetOpen}
          setOpen={handleNestedSheetOpenChange}
          zIndex={10001}
          onDismiss={nestedBottomSheet?.onDismiss}
          titleId={nestedBottomSheet.drawerTitleId}
          dismissible={nestedBottomSheet.dismissible ?? true}
          focusDrawerOnOpen={nestedBottomSheet.focusDrawerOnOpen}
        >
          {typeof nestedBottomSheet.content === 'function'
            ? (nestedBottomSheet.content as Function)()
            : nestedBottomSheet.content}
        </VaulDrawer>
      )}
    </BottomSheetContext.Provider>
  )
}
